"use client"

/**
 * Client-side Logger. Sends logs to Datadog (via /api/logs/frontend proxy).
 * Use in client components: import { Logger } from "@/lib/logger-client"
 * Use useLogger() for user-scoped logs (includes user_id when logged in).
 */

import { sendToDatadogBrowser } from "./datadog-browser"
import { deviceArchLabel } from "./device-arch"
import { shouldSampleLog } from "./log-sampling"
import { getSessionUtm } from "./utm-session"
import { redactAnalyticsPath } from "./analytics/redact-url"
import {
  generateBrowserSpanId,
  generateBrowserTraceId,
  getBrowserSessionId,
  getBrowserVisitor,
} from "./browser-observability-identity"

type LogLevel = "info" | "warn" | "error"
type LogData = Record<string, unknown> | Error | undefined

// Coarse OS bucket emitted on every browser log so the dashboard's "Website
// platform split" widget can break audience down by Mac/Windows/Linux/iOS/
// Android without server-side UA-parsing. Same taxonomy the desktop loggers
// use, so cross-surface queries on @platform stay consistent. Computed once
// at module load — never changes during the session.
function detectPlatform(): string | null {
  if (typeof navigator === "undefined") return null
  const uaData = (navigator as { userAgentData?: { platform?: string } }).userAgentData
  const ua = (navigator.userAgent || "").toLowerCase()
  // UA-Client-Hints is preferred (Chrome/Edge) — but its `platform` reports
  // "Linux" for Android, so cross-check the UA string for the Android token.
  if (uaData?.platform) {
    const p = uaData.platform.toLowerCase()
    if (/android/.test(ua)) return "android"
    if (p.includes("mac")) return "mac"
    if (p.includes("win")) return "windows"
    if (p.includes("linux")) return "linux"
    if (p.includes("ios") || p.includes("iphone") || p.includes("ipad")) return "ios"
    return p
  }
  // Legacy fallback (Safari/Firefox have no UA-CH support yet).
  if (/iphone|ipad|ipod/.test(ua)) return "ios"
  if (/android/.test(ua)) return "android"
  if (/mac/.test(ua)) return "mac"
  if (/windows|win32|win64/.test(ua)) return "windows"
  if (/linux/.test(ua)) return "linux"
  return "unknown"
}
const PLATFORM = detectPlatform()

// Exact silicon (e.g. "mac-arm64", "mac-x64", "windows-x64"). UA-Client-Hints
// high-entropy is async, so seed a best-effort label now and refine it once it
// resolves — almost all logs after the first tick carry the precise value.
let DEVICE_ARCH = deviceArchLabel(PLATFORM, null)
;(function resolveDeviceArch() {
  if (typeof navigator === "undefined") return
  const uaData = (
    navigator as {
      userAgentData?: {
        getHighEntropyValues?: (
          hints: string[]
        ) => Promise<{ architecture?: string; bitness?: string }>
      }
    }
  ).userAgentData
  uaData
    ?.getHighEntropyValues?.(["architecture", "bitness"])
    .then((v) => {
      DEVICE_ARCH = deviceArchLabel(PLATFORM, v.architecture, v.bitness)
    })
    .catch(() => {})
})()

// One id per tab/page-load so all logs from a single browsing session group
// together in Datadog (ties a user's actions across pages into one trail).
// Keep 100% of warn/error and ordinary info; only high-volume info heartbeats
// are rate-sampled. Default 1 (no sampling) until a rate is chosen; tune with
// NEXT_PUBLIC_LOG_INFO_SAMPLE_RATE (e.g. "0.1" to keep 10% of heartbeats).
const INFO_SAMPLE_RATE = (() => {
  const n = Number(process.env.NEXT_PUBLIC_LOG_INFO_SAMPLE_RATE ?? 1)
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : 1
})()

function buildProperties(
  level: LogLevel,
  message: string,
  data?: LogData,
  extraContext?: Record<string, unknown>
): Record<string, unknown> {
  const traceId =
    typeof extraContext?.trace_id === "string" ? extraContext.trace_id : generateBrowserTraceId()
  const spanId =
    typeof extraContext?.span_id === "string" ? extraContext.span_id : generateBrowserSpanId()
  const properties: Record<string, unknown> = {
    level,
    message,
    timestamp: new Date().toISOString(),
    source: "browser",
    // Distinguishes website-frontend logs from desktop-renderer logs and
    // server-side route logs in Datadog. The /api/logs/frontend proxy
    // preserves whatever service tag arrives in the payload.
    service: "phantomcode-website",
    platform: PLATFORM,
    device_arch: DEVICE_ARCH,
    // Global React/browser stacks often contain only framework frames. Capture
    // the route at emission time so a later navigation cannot misattribute it.
    // Never include query credentials or the bearer token in a booking URL.
    page_path:
      typeof window !== "undefined"
        ? redactAnalyticsPath(window.location.pathname)
        : undefined,
    trace_id: traceId,
    span_id: spanId,
    correlation_id: traceId,
    session_id: getBrowserSessionId(),
    ...getBrowserVisitor(),
    // First-touch campaign attribution (utm_source / _medium / _campaign /
    // _content / _term) — stamped on every log so sessions, page views, and
    // conversions can be grouped by acquisition source in Datadog. Empty
    // object (no keys added) when the session has no campaign.
    ...getSessionUtm(),
  }

  if (extraContext) {
    Object.assign(properties, extraContext)
  }

  if (data !== undefined) {
    if (data instanceof Error) {
      properties.error_name = data.name
      properties.error_message = data.message
      properties.error_stack = data.stack
    } else if (typeof data === "object" && data !== null) {
      Object.assign(properties, data)
    }
  }

  return properties
}

function safeCapture(
  level: LogLevel,
  message: string,
  data?: LogData,
  extraContext?: Record<string, unknown>
) {
  try {
    // Drop only high-volume info heartbeats per the sample rate; warn/error and
    // ordinary info always pass.
    if (!shouldSampleLog(level, message, INFO_SAMPLE_RATE, Math.random())) return
    const properties = buildProperties(level, message, data, extraContext)
    sendToDatadogBrowser(level, message, properties)
  } catch {
    // Never throw – logging must not break the app
  }
}

function createLogger(extraContext?: Record<string, unknown>) {
  return {
    info(message: string, data?: LogData) {
      safeCapture("info", message, data, extraContext)
    },
    warn(message: string, data?: LogData) {
      safeCapture("warn", message, data, extraContext)
    },
    error(message: string, data?: LogData) {
      safeCapture("error", message, data, extraContext)
    },
  }
}

/**
 * Returns a logger for client components.
 * Pass extraContext (e.g. { user_id }) to include in all logs – use for filtering by user in Datadog.
 *
 * @example
 * const logger = Logger()
 * logger.info("Button clicked", { buttonId: "submit" })
 *
 * const userLogger = Logger({ user_id: userId })
 * userLogger.error("Checkout failed", error)
 */
function Logger(extraContext?: Record<string, unknown>) {
  return createLogger(extraContext)
}

Logger.info = (message: string, data?: LogData) => {
  safeCapture("info", message, data)
}
Logger.warn = (message: string, data?: LogData) => {
  safeCapture("warn", message, data)
}
Logger.error = (message: string, data?: LogData) => {
  safeCapture("error", message, data)
}

export { Logger }
